
import React from 'react';
import { UserProfile } from '../../types';
import { ArrowRightLeft, User, ArrowRight, XCircle, Zap, RefreshCw } from 'lucide-react';
import { supabase } from '../../services/supabaseClient';
import { forceTransferByAdmin } from '../../services/cloudService';
import type { ListProject } from '../../services/listProject';

interface Props {
    projects: ListProject[];
    users: UserProfile[];
    // Der handelnde Admin: landet als Autor in der Projektnotiz des Transfers,
    // damit im Protokoll ein realer Name und nicht 'System-Admin' steht.
    currentUser?: UserProfile | null;
    onRefresh: () => void;
}

export const AdminTransferList: React.FC<Props> = ({ projects, users, currentUser, onRefresh }) => {
    // Only show projects pending transfer
    const transferProjects = projects.filter(p => p.transfer_target_id);

    const resolveUserName = (id: string) => {
        const u = users.find(user => user.id === id);
        return u ? `${u.first_name} ${u.last_name} (${u.company_name || 'Keine Firma'})` : 'Unbekannter Nutzer';
    };

    const cancelTransfer = async (projectId: string) => {
        if(!confirm("Transfer abbrechen?")) return;
        
        try {
            const { error } = await supabase
                .from('projects')
                .update({ transfer_target_id: null })
                .eq('id', projectId);
            
            if(error) throw error;
            onRefresh();
        } catch (e: any) {
            console.error(e);
            alert("Fehler beim Abbrechen: " + e.message);
        }
    };

    const handleForceTransfer = async (projectId: string, targetUserId: string, isSelfTransfer: boolean) => {
        const msg = isSelfTransfer 
            ? "Dies ist ein Selbst-Transfer. Der Eintrag wird lediglich bereinigt."
            : "Projekt sofort übertragen? Der Ziel-Nutzer wird benachrichtigt.";

        if(!confirm(msg)) return;

        try {
            await forceTransferByAdmin(projectId, targetUserId, currentUser);
            onRefresh();
        } catch (e: any) {
            console.error(e);
            alert("Fehler beim Transfer: " + e.message);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden max-w-5xl mx-auto">
            <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                 <div>
                    <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                        <ArrowRightLeft className="w-5 h-5 text-indigo-600"/> Laufende Projekt-Transfers
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">Übersicht aller Projekte, die aktuell zur Übergabe anstehen.</p>
                 </div>
                 <div className="flex items-center gap-3">
                    <button onClick={onRefresh} className="p-2 hover:bg-slate-200 rounded-full text-slate-500" title="Aktualisieren">
                        <RefreshCw className="w-4 h-4"/>
                    </button>
                    <div className="text-xs font-bold bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full border border-indigo-100">
                        {transferProjects.length} Offen
                    </div>
                 </div>
            </div>

            {transferProjects.length === 0 ? (
                <div className="p-16 text-center flex flex-col items-center justify-center text-slate-400">
                    <ArrowRightLeft className="w-12 h-12 mb-4 opacity-20" />
                    <p className="font-medium">Keine offenen Transfers.</p>
                    <p className="text-xs mt-1">Alle Projekte sind ihren Besitzern fest zugeordnet.</p>
                </div>
            ) : (
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-xs border-b border-slate-200">
                        <tr>
                            <th className="p-4 pl-6">Projekt</th>
                            <th className="p-4">Aktueller Besitzer</th>
                            <th className="p-4 text-center"></th>
                            <th className="p-4">Empfänger (Ziel)</th>
                            <th className="p-4 text-right pr-6">Aktion</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {transferProjects.map(p => {
                            const isSelfTransfer = p.user_id === p.transfer_target_id;
                            
                            return (
                                <tr key={p.id} className="hover:bg-slate-50 group">
                                    <td className="p-4 pl-6">
                                        <div className="font-bold text-slate-800">{p.name}</div>
                                        <div className="text-xs text-slate-500 font-mono mt-0.5">{p.data?.metadata?.projectNumber || 'Keine Nr.'}</div>
                                    </td>
                                    <td className="p-4 text-slate-600">
                                        <div className="flex items-center gap-2">
                                            <User className="w-4 h-4 text-slate-400" />
                                            {resolveUserName(p.user_id)}
                                        </div>
                                    </td>
                                    <td className="p-4 text-center">
                                        <ArrowRight className={`w-4 h-4 mx-auto ${isSelfTransfer ? 'text-red-300' : 'text-indigo-300'}`} />
                                    </td>
                                    <td className="p-4">
                                        <div className={`flex items-center gap-2 font-medium px-2 py-1 rounded w-fit ${isSelfTransfer ? 'text-red-700 bg-red-50' : 'text-indigo-700 bg-indigo-50'}`}>
                                            <User className="w-4 h-4" />
                                            {resolveUserName(p.transfer_target_id)}
                                        </div>
                                        {isSelfTransfer && <div className="text-[11px] text-red-500 mt-1 font-bold">Identisch (Fehler)</div>}
                                    </td>
                                    <td className="p-4 text-right pr-6">
                                        <div className="flex justify-end gap-2">
                                            <button 
                                                onClick={() => handleForceTransfer(p.id, p.transfer_target_id, isSelfTransfer)} 
                                                className={`px-3 py-1.5 rounded-lg text-xs font-bold border border-transparent transition-all flex items-center gap-2 ${
                                                    isSelfTransfer 
                                                    ? 'text-amber-600 hover:bg-amber-50 hover:border-amber-200' 
                                                    : 'text-emerald-600 hover:bg-emerald-50 hover:border-emerald-200'
                                                }`}
                                                title={isSelfTransfer ? "Status bereinigen" : "Transfer sofort durchführen"}
                                            >
                                                {isSelfTransfer ? <RefreshCw className="w-4 h-4"/> : <Zap className="w-4 h-4" />}
                                                {isSelfTransfer ? "Bereinigen" : "Sofort übertragen"}
                                            </button>
                                            <button 
                                                onClick={() => cancelTransfer(p.id)} 
                                                className="text-red-600 hover:bg-red-50 hover:border-red-200 px-3 py-1.5 rounded-lg text-xs font-bold border border-transparent transition-all flex items-center gap-2"
                                            >
                                                <XCircle className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};